
import React from 'react';
import { motion } from 'framer-motion';
import { 
  Building, 
  Home, 
  MapPin, 
  TrendingUp, 
  BedDouble, 
  Landmark, 
  HomeIcon, 
  ArrowDown, 
  ArrowUp 
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  BarChart, 
  Bar, 
  ResponsiveContainer, 
  XAxis, 
  YAxis, 
  Tooltip, 
  Cell 
} from 'recharts';

const HeroAnalysisSample = () => {
  // Sample data for the charts and metrics
  const priceComparison = [{
    name: 'Este Imóvel',
    value: 850000,
    isMain: true
  }, {
    name: 'Média Bairro',
    value: 920000
  }, {
    name: 'Média 2 Quartos',
    value: 870000
  }, {
    name: 'Menor Preço',
    value: 750000
  }, {
    name: 'Maior Preço',
    value: 1150000
  }];

  // Calculate score based on price difference from average
  const avgPrice = priceComparison.find(item => item.name === 'Média Região')?.value || 0;
  const priceDiffPercent = (priceComparison[0].value - avgPrice) / avgPrice * 100;
  const isPriceBelowAvg = priceComparison[0].value < avgPrice;
  const score = isPriceBelowAvg ? 85 : 70;

  // Additional comparison data
  const neighborhoodComparison = {
    name: 'Jardim dos Estados',
    thisProperty: 850000,
    average: 950000,
    diff: -10.5
  };
  const propertyTypeComparison = {
    type: 'Apartamento',
    thisProperty: 8500,
    // per m²
    average: 9200,
    diff: -7.6
  };
  const bedroomComparison = {
    count: 3,
    thisProperty: 850000,
    average: 890000,
    diff: -4.5
  };

  // Format value for display
  const formatValue = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 w-full max-w-md mx-auto"
    >
      {/* Header with Property Score */}
      <div className="bg-blue-50 p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Building className="h-5 w-5 text-blue-dark" />
            <h3 className="font-semibold text-blue-dark">Análise de Imóvel</h3>
          </div>
          <div className="bg-white px-3 py-1 rounded-full text-sm font-medium shadow-sm border border-gray-200 flex items-center">
            <span className={`mr-1 ${score >= 80 ? 'text-green-500' : 'text-yellow-500'}`}>
              {score}
            </span>
            <span className="text-gray-500">/100</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Property Info Card */}
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <Home className="h-5 w-5 text-blue-dark mt-0.5 flex-shrink-0" />
              <div className="flex-grow">
                <h4 className="font-medium text-sm">Apartamento - 100m²</h4>
                <div className="flex items-center mt-1 text-gray-500 text-xs">
                  <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                  <span className="truncate">Jardim dos Estados, Campo Grande</span>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <div className="text-lg font-semibold text-blue-dark">
                    R$ 850.000
                  </div>
                  <div className="text-xs text-gray-500">
                    R$ 8.500/m²
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Market Comparisons Card */}
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <h4 className="font-medium text-sm mb-3 flex items-center">
              <TrendingUp className="h-4 w-4 text-blue-dark mr-2 flex-shrink-0" />
              Comparação de Mercado
            </h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              {/* Neighborhood comparison */}
              <div className="border border-gray-100 rounded-lg p-2">
                <div className="flex items-center space-x-1 mb-1">
                  <Landmark className="h-3 w-3 text-blue-dark flex-shrink-0" />
                  <span className="font-medium text-xs">Bairro</span>
                </div>
                <div className="text-xs text-gray-600 truncate">{neighborhoodComparison.name}</div>
                <div className="flex items-center mt-1">
                  <span className={`text-xs font-medium ${neighborhoodComparison.diff < 0 ? 'text-green-500' : 'text-red-500'} flex items-center`}>
                    {neighborhoodComparison.diff < 0 ? <ArrowDown className="h-3 w-3 mr-0.5" /> : <ArrowUp className="h-3 w-3 mr-0.5" />}
                    {Math.abs(neighborhoodComparison.diff).toFixed(1)}%
                  </span>
                  <span className="text-xs text-gray-500 ml-1">vs. média</span>
                </div>
              </div>

              {/* Property type comparison */}
              <div className="border border-gray-100 rounded-lg p-2">
                <div className="flex items-center space-x-1 mb-1">
                  <HomeIcon className="h-3 w-3 text-blue-dark flex-shrink-0" />
                  <span className="font-medium text-xs">Tipo</span>
                </div>
                <div className="text-xs text-gray-600 truncate">{propertyTypeComparison.type}</div>
                <div className="flex items-center mt-1">
                  <span className={`text-xs font-medium ${propertyTypeComparison.diff < 0 ? 'text-green-500' : 'text-red-500'} flex items-center`}>
                    {propertyTypeComparison.diff < 0 ? <ArrowDown className="h-3 w-3 mr-0.5" /> : <ArrowUp className="h-3 w-3 mr-0.5" />}
                    {Math.abs(propertyTypeComparison.diff).toFixed(1)}%
                  </span>
                  <span className="text-xs text-gray-500 ml-1">vs. média</span>
                </div>
              </div>

              {/* Bedroom count comparison */}
              <div className="border border-gray-100 rounded-lg p-2">
                <div className="flex items-center space-x-1 mb-1">
                  <BedDouble className="h-3 w-3 text-blue-dark flex-shrink-0" />
                  <span className="font-medium text-xs">{bedroomComparison.count} Quartos</span>
                </div>
                <div className="text-xs text-gray-600 truncate">vs. média</div>
                <div className="flex items-center mt-1">
                  <span className={`text-xs font-medium ${bedroomComparison.diff < 0 ? 'text-green-500' : 'text-red-500'} flex items-center`}>
                    {bedroomComparison.diff < 0 ? <ArrowDown className="h-3 w-3 mr-0.5" /> : <ArrowUp className="h-3 w-3 mr-0.5" />}
                    {Math.abs(bedroomComparison.diff).toFixed(1)}%
                  </span>
                  <span className="text-xs text-gray-500 ml-1">vs. média</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Price Comparison Chart Card */}
        <Card className="shadow-sm overflow-hidden">
          <CardContent className="p-4">
            <h4 className="text-sm font-medium mb-3">Comparação de Preço</h4>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={priceComparison} 
                  margin={{ top: 5, right: 5, left: 5, bottom: 25 }}
                  layout="vertical"
                >
                  <XAxis 
                    type="number" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => value >= 1000000 ? `${(value/1000000).toFixed(1)}M` : `${(value/1000).toFixed(0)}K`}
                    domain={[600000, 1200000]}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    tick={{ fontSize: 10 }}
                    width={80}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    formatter={(value) => formatValue(Number(value))}
                    contentStyle={{
                      borderRadius: '4px',
                      border: '1px solid #eee',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      fontSize: '11px',
                    }}
                  />
                  <Bar 
                    dataKey="value" 
                    radius={[0, 4, 4, 0]}
                  >
                    {priceComparison.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.isMain ? '#1A5F7A' : '#57C5B6'} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="bg-blue-50 px-4 py-3 border-t border-gray-200 text-center">
        <p className="text-xs text-blue-dark">
          Analise um imóvel agora e acesse o relatório completo!
        </p>
      </div>
    </motion.div>
  );
};

export default HeroAnalysisSample;
